<template>
  <div
    class="flex items-start justify-start md:justify-center md:mx-6 my-9 p-5 md:p-10 rounded-xl shadow-[0_4px_44px_0_rgba(178,178,178,0.16)]"
  >
    <div class="overflow-hidden flex flex-col items-stretch justify-start gap-8">
      <div class="text-xl text-blackG2 font-bold">
        {{ $t('verificationStatus.yourVerificationStatus') }}
      </div>
      <div class="flex items-stretch justify-start gap-8 overflow-x-auto md:justify-center md:flex-wrap">
        <KycStatusCard
          v-for="(stepItem, index) in steps"
          :key="stepItem.step"
          :isBlueTheme="index === 0"
          :kycStatus="stepItem.kycStatus"
          :title="stepItem.title"
          :verifyList="stepItem.verifyList"
          :isVerified="currStep > stepItem.step"
          :isStepDisabled="currStep > stepItem.step"
          :permissionKeys="stepItem.permissionKeys"
          :verifyButtonOnClick="stepItem.verifyButtonOnClick"
          :verifyButtonTip="stepItem.verifyButtonTip"
          :verifyListMaxLength="verifyListMaxLength"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { RegisterRoutePath } from '@/router/registerRoutes';
import auditStatus from '@/constants/auditStatus';
import registerStep from '@/constants/register/registerStep';
import KycStatusCard, { KycPermissionKey, KycStatus } from '@/components/register/KycStatusCard.vue';
import RegisterForm from '@/components/register/registerForms/RegisterForm.vue';
import RegisterTemplate from '@/components/register/registerTemplate/RegisterTemplate.vue';

export default {
  name: 'VerificationStatus',
  components: { KycStatusCard, RegisterForm, RegisterTemplate },
  data() {
    return {
      stepperDirection: 'horizontal'
    };
  },
  computed: {
    steps() {
      return [
        {
          kycStatus:
            this.currStep < registerStep.personalInformation ||
            // Audit rejected
            this.accountAuditStatus === auditStatus.rejected
              ? KycStatus.disabled
              : this.accountAuditStatus === auditStatus.completed
              ? KycStatus.completed
              : this.hasAccountAuditPending
              ? KycStatus.inProgress
              : this.accountAuditStatus === auditStatus.init || this.accountAuditStatus === auditStatus.submitted
              ? KycStatus.active
              : KycStatus.disabled,
          title: 'verificationStatus.accountOpening.title',
          verifyList: [
            {
              condition: this.hasAccountAuditPending || this.accountAuditStatus === auditStatus.completed,
              title: 'verificationStatus.accountOpening.yourBasicInformation',
              isVerifiedItem: false
            },
            {
              condition: this.accountAuditStatus === auditStatus.completed,
              title: 'verificationStatus.accountOpening.tradingAccountActivation',
              isVerifiedItem: false
            },
            {
              condition: this.accountAuditStatus === auditStatus.completed,
              title: '',
              isHide: this.hasAccountAuditPending,
              isVerifiedItem: true
            }
          ],
          permissionKeys: [KycPermissionKey.mtAccount, KycPermissionKey.deposit],
          verifyButtonOnClick: () => this.$router.push(RegisterRoutePath.openAccount),
          verifyButtonTip: ''
        },
        {
          kycStatus:
            this.currStep < registerStep.idInformation ||
            // Audit rejected
            this.accountAuditStatus === auditStatus.rejected
              ? KycStatus.disabled
              : // id3Pass || POI completed
              this.idPass
              ? KycStatus.completed
              : // POI pending / POI reaudit
              this.isPoiUnderReview
              ? KycStatus.inProgress
              : // POI init / POI rejected
              this.isPoiEditable
              ? KycStatus.active
              : KycStatus.disabled,
          title: 'verificationStatus.idVerification.title',
          verifyList: [
            {
              condition: this.isPoiUnderReview || this.poiAuditStatus === auditStatus.pending || this.idPass,
              title: 'verificationStatus.idVerification.yourIdentificationDocument',
              isVerifiedItem: false
            },
            {
              condition: this.idPass,
              title: '',
              isHide: this.isPoiUnderReview || this.isPoiResubmit,
              isVerifiedItem: true
            }
          ],
          permissionKeys: [KycPermissionKey.mtAccount, KycPermissionKey.deposit, KycPermissionKey.trade],
          verifyButtonOnClick: async () => { 
            await this.$store.dispatch('common/actionPoiAuditStatus');

            if(this.poiAuditStatus === auditStatus.completed)  {
              return
            }
           
            this.$router.push(RegisterRoutePath.identityVerification)
          },
          verifyButtonTip: ''
        },
        {
          kycStatus:
            this.currStep < registerStep.poaInformation ||
            // Audit rejected
            this.accountAuditStatus === auditStatus.rejected
              ? KycStatus.disabled
              : // POA completed
              this.poaPass
              ? KycStatus.completed
              : // POA pending / POA reaudit
              this.isPoaUnderReview
              ? KycStatus.inProgress
              : // POA init / POA rejected
              this.isPoaEditable
              ? KycStatus.active
              : KycStatus.disabled,
          title: 'verificationStatus.poaVerification.title',
          verifyList: [
            {
              condition: this.isPoaUnderReview || this.poaAuditStatus === auditStatus.pending || this.poaPass,
              title: 'verificationStatus.poaVerification.yourIdentificationDocument',
              isVerifiedItem: false
            },
            {
              condition: this.poaPass,
              title: '',
              isHide: this.isPoaUnderReview || this.isPoaResubmit,
              isVerifiedItem: true
            }
          ],
          permissionKeys: [
            KycPermissionKey.mtAccount,
            KycPermissionKey.deposit,
            KycPermissionKey.trade,
            KycPermissionKey.withdrawal
          ],
          verifyButtonOnClick: async () => {
            await this.$store.dispatch('common/actionPoaAuditStatus');
            if(this.poaAuditStatus === auditStatus.completed)  {
              return
            }
            this.$router.push(RegisterRoutePath.poaVerification)
          },
          verifyButtonTip: 'verificationStatus.poaVerification.verifyButtonTip'
        }
      ];
    },
    verifyListMaxLength() {
      return this.steps.reduce((max, step) => Math.max(max, step.verifyList.length), 0);
    },
    accountAuditStatus() {
      return this.$store.state.common.accountAuditStatus;
    },
    ...mapState('register', ['currStep']),
    ...mapState('common', ['poiAuditStatus', 'poaAuditStatus']),
    ...mapGetters('common', [
      'idPass',
      'poaPass',
      'hasAccountAuditPending',
      'isPoiUnderReview',
      'isPoaUnderReview',
      'isPoiEditable',
      'isPoaEditable',
      'isPoiResubmit',
      'isPoaResubmit'
    ])
  },
  mounted() {
    this.$store.dispatch('register/getCurrentStep');
    if (this.accountAuditStatus !== auditStatus.completed) {
      this.$store.dispatch('common/actionAccountAuditStatus');
    }
    this.$store.dispatch('common/actionPoiAuditStatus');
    this.$store.dispatch('common/actionPoaAuditStatus');
  }
};
</script>

<style lang="scss" scoped>
$stepper-width: 4.5rem;
$stepper-color: $primary-blue;
$stepper-line-color: #aaaaaf;

.register ::v-deep .el-steps {
  align-items: stretch;
  .el-step {
    &.is-horizontal {
      display: flex;
      flex: 1;
      width: 0;
      flex-direction: column;
      .el-step__line {
        top: 2.125rem;
        height: 1px;
      }

      .el-step__main {
        flex: 1;
        margin-top: 3rem;
      }

      .el-step__description {
        display: flex;
        justify-content: center;
      }
    }
    &.is-vertical {
      .el-step__line {
        width: 1px;
        @include rtl-sass-prop(left, right, calc(#{$stepper-width / 2}));
        top: calc(#{$stepper-width + $stepper-width / 2});
        bottom: calc(#{$stepper-width - $stepper-width / 2});
      }
      .el-step__main {
        flex: 1;
        @include rtl-sass-prop(margin-left, margin-right, 3rem);
        margin-bottom: calc(#{$stepper-width / 2});
      }
    }
    &.is-center {
      .el-step__line {
        @include rtl-sass-prop(left, right, calc(50% + #{$stepper-width}));
        @include rtl-sass-prop(right, left, calc(-50% + #{$stepper-width}));
      }

      .el-step__description {
        padding: 0;
      }
    }
    .el-step__line {
      background-color: $stepper-line-color;
      border-color: $stepper-line-color;
    }
    .el-step__line-inner {
      display: none;
    }
    .el-step__head {
      color: white;
      border-color: $stepper-color;
      &.is-finish {
        color: white;
        border-color: $stepper-color;
      }

      .el-step__icon {
        width: $stepper-width;
        height: $stepper-width;
        background: $stepper-color;
        .el-step__icon-inner {
          font-size: 2rem;
          font-weight: normal;
        }
      }
    }

    .el-step__description {
      display: flex;
      height: 100%;
      margin-left: 1rem;
      margin-right: 1rem;
      color: black;
      padding: 0;

      &.is-finish,
      &.is-success {
        color: black;
      }
    }
  }
}
</style>
